import {Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject} from '@angular/core';
import {IfsProductType} from '../../../../_constants/types';
import {HawkRequest, Ifs2, IfsRequest, newIfs2Address} from '../../../../_data/reports/ifs/ifs-request';
import {ConsumerRequestReport} from '../ifs-report.component';
import {isConsumerRequestReport, isIfsRequest} from '../../../../_utils/type-guards';
import {dayMonthYear, equalsAnyIgnoreCase, isBlank, nullIfBlank, reformatDate, reformatPhone, removeExtraWhitespace} from '../../../../_utils/lang';
import {Dirs, IfsReport} from '../../../../_data/reports/ifs/ifs-report';
import {ConsumerStatement, DualSource, EnhancedDirectoryServices} from '../../../../_data/reports/consumer/consumer-report';
import {isAnyProduct} from '../../../../_utils/products';
import {Observable} from 'rxjs';
import {I18nService} from '../../../../_services/gui/i18n.service';
import {creditFreezeMessage, hasHrfa, hrfa, isConsumerStatementExists, isCreditFreezeAndIsNonExemptedMC, isCreditFreezeMessageExists, isHrfaClear} from 'src/app/_utils/consumer';
import {AsyncPipe, KeyValuePipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-ifs-report-content',
    templateUrl: './ifs-report-content.component.html',
    styleUrls: ['./ifs-report-content.component.scss'],
    imports: [AsyncPipe, KeyValuePipe, TranslateModule]
})
export class IfsReportContentComponent {

  @Input() showInquirySection: boolean = true;

  @Output() creditReport: EventEmitter<ElementRef> = new EventEmitter();
  @Output() thisOrder: EventEmitter<{lang; ref; branch; resultsIssued?}> = new EventEmitter();

  i18nService = inject(I18nService);

  isHrfaClear = isHrfaClear;
  hasHrfa = hasHrfa;
  hrfa = hrfa;
  reformatDate = reformatDate;
  dayMonthYear = dayMonthYear;

  ifsRequest: IfsRequest;

  ifs2: Ifs2;
  enhancedDirectoryServices: EnhancedDirectoryServices;

  last4DigitsOfSin: string;
  nameOfPerson: string;
  reformatPhone = reformatPhone;
  isCreditFreezeAndIsNonExemptedMC = isCreditFreezeAndIsNonExemptedMC;
  isCreditFreezeMessageExists = isCreditFreezeMessageExists;
  creditFreezeMessage = creditFreezeMessage;
  phone: string;
  hawk: HawkRequest[] = [];
  consumerRequestReport: ConsumerRequestReport;
  dualSources: DualSource[] = [];

  private _ifsReport: IfsReport;
  private inputData: IfsRequest | ConsumerRequestReport;

  private processIfsRequest(ifsRequest: IfsRequest) {
    console.info('Processing IFS request:', ifsRequest);
    console.info('Stored IFS Report:', this._ifsReport);
    this.ifsRequest = ifsRequest;
    this.ifs2 = this.ifsRequest.ifs2;
    this.last4DigitsOfSin = this.ifsRequest?.bvss?.sin;
    this.hawk = this.ifsRequest?.hawk ?? [];
    this.nameOfPerson = ifsRequest.ifs2?.name;
    this.emitOrderMetaData();
  }

  private processAmlRequest(consumerRequestReport: ConsumerRequestReport) {

    console.info('Processing AML request');

    this.consumerRequestReport = consumerRequestReport;
    const consumerRequest = this.consumerRequestReport?.req;
    console.debug(`AML request: ${JSON.stringify(consumerRequest)}`);

    const requestConsumer = consumerRequest?.consumers?.[0];
    const addr = requestConsumer.addresss?.[0];
    this.nameOfPerson = removeExtraWhitespace(`${requestConsumer.firstName} ${requestConsumer.middleName} ${requestConsumer.lastName}`);
    this.ifs2 = {
      name:      this.nameOfPerson,
      dob:       requestConsumer.dob,
      phone:     requestConsumer.phones?.[0]?.phoneNumber,
      addresses: addr && [newIfs2Address(null, addr?.apt, addr.civic, addr.street, addr.city, addr.province?.toUpperCase(), addr.postal, null)]
    };
    this.thisOrder.emit({
      branch: consumerRequest.metaInfo.branchName,
      lang:   consumerRequest.metaInfo.lang,
      ref:    consumerRequest.metaInfo.userReference
    });

    this.last4DigitsOfSin = requestConsumer?.sin;
    this.enhancedDirectoryServices = this.consumerRequestReport?.report?.consumers?.[0]?.enhancedDirectoryServices;
    this.dualSources = this.consumerRequestReport?.report?.consumers?.[0]?.dualSource;

  }

  /**
   * Checks for both ifsReport and ifsRequest to be set.  Both parts of this class that deal with those will call this method.
   * Meaning that whichever one runs second will cause this to actually emit.
   */
  emitOrderMetaData(): void {
    if(this._ifsReport && this.ifsRequest) {
      this.thisOrder.emit({
        branch: this.ifsRequest.ifs?.branchName,
        lang:   this.ifsRequest.ifs?.lang,
        ref:    this._ifsReport.ifs?.userReference
      });
    }
  }

  getProductReturned(): IfsProductType | null {
    if(this._ifsReport?.hawk && this.consumerRequestReport) {
      return 'eAml';
    } else if(this.isCreditFreezeMessageExistsInIFSReport()) {
      return this.getProductReturnedWhenResponseIsEmpty();
    } else if(this._ifsReport) {
      const r = this._ifsReport;
      const rq = this.ifsRequest;
      return (!!r.avs && 'avs') ||
        (!!r.bvs && 'bvs') ||
        ((!!r.dirs || !!r.dirs2) && !!rq.dirsLookup && 'dirsLookup') ||
        ((!!r.dirs || !!r.dirs2) && !!rq.dirsEnhanced && 'eDirs') ||
        ((!!r.dirs || !!r.dirs2) && 'dirs') ||
        (!!r.hawk && 'hawk') ||
        null;
    } else if(this.consumerRequestReport) {
      return 'aml';
    } else {
      return null;
    }
  }

  getProductReturnedWhenResponseIsEmpty(): IfsProductType {
    if(this.ifsRequest.avs) {
      return 'avs';
    } else if(this.ifsRequest.bvs) {
      return 'bvs';
    } else if(this.ifsRequest.dirsLookup) {
      return 'dirsLookup';
    } else if(this.ifsRequest.dirsEnhanced) {
      return 'eDirs';
    } else if(this.ifsRequest.hawk) {
      return 'hawk';
    } else if(this.consumerRequestReport) {
      return 'aml';
    }
  }

  isNoHit(dirs: Dirs): boolean {
    return this.getProductReturned() === 'dirsLookup' && nullIfBlank(dirs.lastName) === null && nullIfBlank(dirs.firstName) === null && nullIfBlank(dirs.street) === null
      && nullIfBlank(dirs.city) === null && nullIfBlank(dirs.prov) === null && nullIfBlank(dirs.postal) === null;
  }

  isReportAnyIfsProduct(products: IfsProductType[]): boolean {
    return isAnyProduct(this.getProductReturned(), products);
  }

  amlBureauFlagTranslation(flag: string): Observable<string> {
    flag = isBlank(flag) ? 'space' : flag.toLowerCase();
    return this.i18nService.getTranslation(`content.keyed.ifs.aml.bureau-flags.${flag}`);
  }

  amlBureauHitFlagTranslation(flag: string): Observable<string> {
    flag = isBlank(flag) ? 'space' : flag.toLowerCase();
    return this.i18nService.getTranslation(`content.keyed.ifs.aml.bureauhit-flags.${flag}`);
  }
  
  amlFlagTranslation(flag: string): Observable<string> {
    flag = isBlank(flag) ? 'space' : flag.toLowerCase();
    return this.i18nService.getTranslation(`content.keyed.ifs.aml.match-flags.${flag}`);
  }

  noOrPartialMatch(match: string): string {
    return (isBlank(match) ? '' : ` - ${match}`);
  }
  
  amlFraudFlagTranslation(flag: string): Observable<string> {
    flag = isBlank(flag) ? 'space' : flag.toLowerCase();
    return this.i18nService.getTranslation(`content.keyed.ifs.aml.fraud-flags.${flag}`);
  }

  @Input('inputData')
  set _inputData(value: IfsRequest | ConsumerRequestReport) {
    this.inputData = value;

    if(isIfsRequest(this.inputData)) {
      this.processIfsRequest(this.inputData);
    } else if(isConsumerRequestReport(this.inputData)) {
      this.processAmlRequest(this.inputData);
    }
  }

  get ifsReport(): IfsReport {
    return this._ifsReport;
  }

  @Input()
  set ifsReport(value: IfsReport) {
    this._ifsReport = value;
    this.emitOrderMetaData();
  }

  isCreditFreezeMessageExistsInIFSReport(): boolean {
    if(this._ifsReport!=null && this._ifsReport.mi01OUT!=null && this._ifsReport.mi01OUT.message !== '' && this._ifsReport.mi01OUT.message.indexOf('FREEZE')!== -1) {
      return true;
    }
  }

  isCreditFreezeMessageExistsInConsumerReport(): boolean {
    return !!this.consumerRequestReport?.report?.consumers.find(c => isCreditFreezeMessageExists(c));
  }

  getCreditFreezeMessageForConsumerReport(): string {
    let message;
    if(this.consumerRequestReport && this.consumerRequestReport.report.consumers) {
      this.consumerRequestReport.report.consumers.forEach(consumer => {
        message = creditFreezeMessage(consumer);
        if(message?.length > 0 ) {
          return message;
        }
      });
    }
    return message;
  }

  isCreditFreezeAndNonExemptedMCInConsumerReport() {
    return !!this.consumerRequestReport?.report?.consumers.find(c => isCreditFreezeAndIsNonExemptedMC(c));
  }

  isEbvsidRequired() {
    return this.isEbvsidNeeded(this.consumerRequestReport?.report?.consumers?.[0]?.enhancedDirectoryServices?.amlBureauMatch);
  }

  isDs04Empty() {
    return this.isDs04Needed(this.consumerRequestReport?.report?.consumers?.[0]?.enhancedDirectoryServices);
  }

  hasConsumerStatement(): boolean {
    return !!this.consumerRequestReport?.report?.consumers.find(c => isConsumerStatementExists(c));
  }

  isEbvsidNeeded(amlBureauMatch): boolean {
    return equalsAnyIgnoreCase(amlBureauMatch, 'S', 'R', 'T');
  }

  isDs04Needed(enhancedDirectoryServices: EnhancedDirectoryServices): boolean {
    return equalsAnyIgnoreCase(enhancedDirectoryServices.amlBureauMatch,  'T');
  }

  consumerStatemets(consumerStmt: ConsumerStatement[]): {message: string; date: string; type: string}[] {
    return [
      ...consumerStmt.map(ms => ({message: ms.statementText, date: ms.remarkDate, type: ms.remarkType}))
    ];
  }

  @ViewChild('creditReport', {read: ElementRef})
  set _creditReport(value: ElementRef) {
    this.creditReport.emit(value);
  }

}
